import { useEffect, useMemo, useState, useRef } from 'react'
import { EMPTY_HOUR, getFitnessStats, mergeHours } from '../Utils/utils'
import { processFitnessAchievements } from '../Utils/processFitnessAchievements'
import { useToast } from '../Toasts/Context/ToastContext'

export const useFitnessTracker = ({ userId, enabled }) => {
  const { addToast } = useToast()
  const [stats, setStats] = useState(null)
  const [rawHours, setRawHours] = useState([])
  const lastHourRef = useRef(new Date().getHours())
  const [error, setError] = useState(false)
  const [expanded, setExpanded] = useState(!enabled)

  const currentHour = stats?.hours?.[new Date().getHours()] ?? null

  const overall = useMemo(() => {
    if (!currentHour) return 0

    const metrics = [
      currentHour.newLeads,
      currentHour.scrapBooked,
      currentHour.salvageBooked,
    ]

    const percentages = metrics.map(({ value, target }) =>
      target > 0 ? value / target : 0
    )

    return (percentages.reduce((a, b) => a + b, 0) / percentages.length) * 100
  }, [currentHour])

  const historyHours = useMemo(() => {
    const hours = rawHours.slice(-7)

    return [
      ...hours,
      ...Array.from({ length: Math.max(0, 7 - hours.length) }, () => ({
        ...EMPTY_HOUR,
      })),
    ]
  }, [rawHours])

  useEffect(() => {
    // NOTE - for testing only, remove this in production
    if (!enabled) {
      return
    }

    let lastFetch = Date.now()

    const fetchData = async () => {
      lastFetch = Date.now()

      try {
        const data = await getFitnessStats()

        const mergedData = {
          ...data,
          hours: mergeHours(data.hours),
        }

        const events = processFitnessAchievements({
          currentHour: mergedData.hours[new Date().getHours()],
          daily: mergedData.daily,
          userId,
        })

        events.forEach((event) => {
          addToast({
            type: 'success',
            ...event,
          })
        })

        setRawHours(data.hours)
        setStats(mergedData)
      } catch (error) {
        console.error(error)
        setError(true)
      }
    }

    const updateTimer = () => {
      const now = new Date()

      const hourChanged = now.getHours() !== lastHourRef.current

      if (hourChanged) {
        lastHourRef.current = now.getHours()
        fetchData()
        return
      }

      const fiveMinutesPassed = Date.now() - lastFetch >= 5 * 60 * 1000

      if (fiveMinutesPassed) {
        fetchData()
      }
    }

    fetchData()
    updateTimer()

    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [addToast, userId])

  return {
    enabled,
    currentHour,
    historyHours,
    overall,
    stats,
    error,
    expanded,
    setExpanded,
  }
}
